import { css, type CSSObject } from '@emotion/react'
import {
  CornerRadius,
  gemVar,
  Spacing,
  ThemedColors,
  Typography,
} from '@opallabs/gem'
import { buttonLikeCSSVariables, buttonLikeSelectors } from '../ButtonLike'
import { base } from '../ButtonLike/styles'
import { type Button2Props } from './types'

const rootClassName = 'lapidary-button2'
export const selectors = {
  root: rootClassName,
} as const

const variants = {
  '&[data-variant=primary]': {
    [buttonLikeCSSVariables.backgroundColor]: gemVar(ThemedColors.Primary6),
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextContrast),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(
      ThemedColors.Primary7
    ),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(
      ThemedColors.Primary8
    ),
  },
  '&[data-variant=secondary]': {
    [buttonLikeCSSVariables.backgroundColor]: gemVar(ThemedColors.Primary2),
    [buttonLikeCSSVariables.strokeColor]: gemVar(ThemedColors.StrokePrimary1),
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextLinks),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(
      ThemedColors.Primary3
    ),
    [buttonLikeCSSVariables.hoverStrokeColor]: gemVar(
      ThemedColors.StrokePrimary1
    ),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(
      ThemedColors.Primary3
    ),
    [buttonLikeCSSVariables.activeStrokeColor]: gemVar(
      ThemedColors.StrokePrimary2
    ),
  },
  '&[data-variant=tertiary]': {
    [buttonLikeCSSVariables.backgroundColor]: gemVar(ThemedColors.Surface1),
    [buttonLikeCSSVariables.strokeColor]: gemVar(ThemedColors.StrokeSecondary2),
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextPrimary),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(
      ThemedColors.Primary2
    ),
    [buttonLikeCSSVariables.hoverStrokeColor]: gemVar(
      ThemedColors.StrokePrimary1
    ),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(
      ThemedColors.Primary2
    ),
    [buttonLikeCSSVariables.activeStrokeColor]: gemVar(
      ThemedColors.StrokePrimary2
    ),
  },
  '&[data-variant=ghost-white]': {
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextPrimary),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(
      ThemedColors.Primary1
    ),
    [buttonLikeCSSVariables.hoverStrokeColor]: gemVar(
      ThemedColors.StrokePrimary1
    ),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(
      ThemedColors.Primary3
    ),
    [buttonLikeCSSVariables.activeStrokeColor]: gemVar(
      ThemedColors.StrokePrimary1
    ),
  },
  '&[data-variant=ghost-blue]': {
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextLinks),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(
      ThemedColors.Primary2
    ),
    [buttonLikeCSSVariables.hoverStrokeColor]: gemVar(
      ThemedColors.StrokePrimary1
    ),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(
      ThemedColors.Primary3
    ),
    [buttonLikeCSSVariables.activeStrokeColor]: gemVar(
      ThemedColors.StrokePrimary1
    ),
  },
  '&[data-variant=ghost-gray]': {
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextPrimary),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(
      ThemedColors.Surface3
    ),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(
      ThemedColors.Surface4
    ),
  },
  '&[data-variant=destructive]': {
    [buttonLikeCSSVariables.backgroundColor]: gemVar(ThemedColors.Error1),
    [buttonLikeCSSVariables.textColor]: gemVar(ThemedColors.TextContrast),
    [buttonLikeCSSVariables.hoverBackgroundColor]: gemVar(ThemedColors.Error2),
    [buttonLikeCSSVariables.activeBackgroundColor]: gemVar(ThemedColors.Error3),
  },
} as const satisfies Record<
  `&[data-variant=${NonNullable<Button2Props['variant']>}]`,
  CSSObject
>

const sizes = {
  '&[data-size=regular]': {
    blockSize: Spacing.M * 2,
    columnGap: Spacing.XS + 1,
    inlineSize: 'fit-content',
    paddingBlock: Spacing.XS * 1.5,
    paddingInline: Spacing.S,

    [`.${buttonLikeSelectors.label}`]: {
      marginInlineEnd: Spacing.XS,
      marginInlineStart: Spacing.XS + 1,
    },
  },
  '&[data-size=small]': {
    blockSize: Spacing.L,
    paddingInline: Spacing.XS,

    [`.${buttonLikeSelectors.label}`]: {
      marginInline: Spacing.XS,
    },
  },
} as const satisfies Record<
  `&[data-size=${NonNullable<Button2Props['size']>}]`,
  CSSObject
>

export const root = css(base, sizes, variants, {
  borderRadius: CornerRadius.Small,

  '&[aria-disabled=true]': {
    cursor: 'default',
  },

  [`.${buttonLikeSelectors.label}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...Typography.T500,
  },
})

export const linkRoot = css({
  textDecoration: 'none',
})
